import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryErrorState } from '../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';
import { apiErrorMessage } from '../../i18n';
import { api } from '../../lib/api';
import { useQueryRetry } from '../../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../../lib/session';
import { normalizePairingCode, isValidPairingCode } from './pairingCode';
import { PairScreenPicker } from './PairScreenPicker';

/**
 * Phone-side confirm for TV-started pairing (scan QR → pick screen → connect).
 */
export function PairDevicePage() {
  const { t } = useTranslation('displays');
  const organisationId = useOrganisationId();
  const [params] = useSearchParams();
  const rawCode = params.get('code') ?? '';
  const code = normalizePairingCode(rawCode);
  const codeOk = isValidPairingCode(code);

  const [screenId, setScreenId] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
    enabled: codeOk,
  });
  const retry = useQueryRetry(() => screensQuery.refetch());

  const screens = screensQuery.data ?? [];
  const unpairedFirst = useMemo(
    () =>
      [...screens].sort((a, b) => {
        const aOpen = a.deviceId == null ? 0 : 1;
        const bOpen = b.deviceId == null ? 0 : 1;
        if (aOpen !== bOpen) return aOpen - bOpen;
        return a.name.localeCompare(b.name);
      }),
    [screens],
  );

  const approve = useMutation({
    mutationFn: () => {
      if (!screenId) throw new Error('Pick a screen');
      return api.approvePairingSession(organisationId, code, { screenId });
    },
    onSuccess: () => setDone(true),
  });

  const approveError = approve.isError
    ? apiErrorMessage(approve.error, t('pair.connectError'))
    : null;

  return (
    <div
      className="min-h-screen px-5 py-10"
      style={{ background: 'var(--gs-bg)', color: 'var(--gs-ink)' }}
    >
      <div className="mx-auto flex w-full max-w-lg flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color: 'var(--gs-muted)' }}>
            {t('pair.eyebrow')}
          </span>
          <h1 className="m-0 text-[28px] font-extrabold tracking-[-0.03em]">
            {done ? t('pair.titleDone') : t('pair.titleChoose')}
          </h1>
          <p className="m-0 text-[15px] leading-snug" style={{ color: 'var(--gs-muted)' }}>
            {done
              ? t('pair.bodyDone')
              : codeOk
                ? t('pair.bodyWithCode', { code })
                : t('pair.bodyNoCode')}
          </p>
        </div>

        {!codeOk ? (
          <p className="m-0 text-sm font-semibold" style={{ color: 'var(--gs-bad)' }} role="alert">
            {t('pair.invalidLink')}
          </p>
        ) : done ? (
          <Link
            to="/screens"
            className="inline-flex items-center justify-center rounded-[10px] px-4 py-3 text-[15px] font-bold no-underline"
            style={{
              color: 'var(--gs-accent-ink)',
              background: 'var(--gs-accent-solid)',
            }}
          >
            {t('pair.backToDisplays')}
          </Link>
        ) : screensQuery.isError || retry.isRetrying ? (
          <QueryErrorState
            message={t('pair.loadError')}
            onRetry={retry.retry}
            isRetrying={retry.isRetrying}
          />
        ) : screensQuery.isPending ? (
          <QueryLoadingBlock />
        ) : unpairedFirst.length === 0 ? (
          <p className="m-0 text-sm" style={{ color: 'var(--gs-muted)' }}>
            {t('pair.noScreens')}
          </p>
        ) : (
          <>
            <PairScreenPicker
              screens={unpairedFirst}
              selectedId={screenId}
              onSelect={setScreenId}
            />
            {approveError ? (
              <p className="m-0 text-sm font-semibold" style={{ color: 'var(--gs-bad)' }} role="alert">
                {approveError}
              </p>
            ) : null}
            <button
              type="button"
              disabled={!screenId || approve.isPending}
              onClick={() => approve.mutate()}
              className="rounded-[10px] border-none px-4 py-3.5 text-[15px] font-bold disabled:opacity-50"
              style={{
                color: 'var(--gs-accent-ink)',
                background: 'var(--gs-accent-solid)',
                fontFamily: 'inherit',
              }}
            >
              {approve.isPending ? t('pair.connecting') : t('pair.connect')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
